import { useCallback, useEffect, useState } from 'react';
import { eventsInWindow, type LifeEvent } from '@loggy/shared';

import { fetchNodeEvents } from '../api.js';

/**
 * Whether this node's chart is currently showing life events, and the events
 * themselves once it is.
 *
 * Off until the reader turns it on, which is the whole point: most of the time
 * a measurement is a measurement, and a page that starts covered in annotations
 * has answered a question nobody asked. Once turned on it stays on for that
 * node, because someone who wants the context for their weight wants it every
 * time they look at their weight.
 *
 * The preference lives in localStorage, per node, by the owner's own choice
 * when this was specified. It therefore does not follow them to their phone —
 * a real cost, accepted in exchange for not adding a column and a migration to
 * remember a tick box.
 *
 * Nothing here can leak: `fetchNodeEvents` is an ordinary node request, so a
 * caller looking at someone else's dashboard only ever gets events from an
 * events node that was actually shared with them. When it was not, there is no
 * `eventsNodeId` to pass in the first place — the node is absent from the orbit
 * they were granted.
 */

const KEY_PREFIX = 'loggy:events-overlay:';

/**
 * Reads the remembered preference.
 *
 * Every access is wrapped: in a private window, or with site data blocked, the
 * accessor itself throws rather than returning null, and a chart that cannot
 * render because of a stored tick box would be a poor trade.
 */
function readPreference(nodeId: string): boolean {
  try {
    return window.localStorage.getItem(`${KEY_PREFIX}${nodeId}`) === 'on';
  } catch {
    return false;
  }
}

function writePreference(nodeId: string, enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(`${KEY_PREFIX}${nodeId}`, 'on');
    else window.localStorage.removeItem(`${KEY_PREFIX}${nodeId}`);
  } catch {
    // The tick still works for this visit; it simply will not be remembered.
  }
}

export interface EventOverlay {
  /** Whether the reader has asked for events on this chart. */
  enabled: boolean;
  toggle: (next: boolean) => void;
  /** The events inside the chart's window. Empty until enabled. */
  events: LifeEvent[];
  /** Set when the events could not be loaded — the chart still draws. */
  error: string | null;
}

export function useEventOverlay(
  /** The node being looked at. Only ever the key the preference is stored under. */
  nodeId: string,
  /** The node holding the events, or null when there is none to show. */
  eventsNodeId: string | null,
  /** The chart's own time span, or null while there is nothing plotted. */
  window_: { from: string; to: string } | null,
): EventOverlay {
  const [enabled, setEnabled] = useState(() => readPreference(nodeId));
  const [events, setEvents] = useState<LifeEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  // A different node is a different preference. Without this, opening weight
  // and then blood pressure would carry the first one's tick to the second.
  useEffect(() => {
    setEnabled(readPreference(nodeId));
    setEvents([]);
    setError(null);
  }, [nodeId]);

  const toggle = useCallback(
    (next: boolean) => {
      setEnabled(next);
      writePreference(nodeId, next);
    },
    [nodeId],
  );

  const from = window_?.from ?? null;
  const to = window_?.to ?? null;

  useEffect(() => {
    if (!enabled || eventsNodeId === null || from === null || to === null) {
      setEvents([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const loaded = await fetchNodeEvents(eventsNodeId, { from, to });
        if (cancelled) return;
        // Filtered again on the way in. The server already narrows to the
        // window, but the chart's span moves with the range picker while a
        // response is in flight, and drawing an event outside the axis would
        // put it on top of the y-labels.
        setEvents(eventsInWindow(loaded, from, to));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setEvents([]);
        // Surfaced, never thrown: the measurements are the page, and losing
        // their context is not a reason to lose them too.
        setError(err instanceof Error ? err.message : 'Could not load life events.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, eventsNodeId, from, to]);

  return { enabled, toggle, events, error };
}
